import { Injectable } from '@angular/core';
import { Table } from 'primeng/table';

export type TableActionPredicate<T> = (action: T) => boolean;

/**
 * Service for managing focus in tables when rows are removed due to user actions.
 * Helps maintain accessibility by moving focus to the next appropriate row
 * instead of losing focus to the top of the page.
 */
@Injectable()
export class TableFocusManagementService {
    private currentActionRowIndex: number = -1;
    private shouldRestoreFocus: boolean = false;

    /**
     * Track the row index that is about to be acted upon
     */
    trackActionRowIndex<T>(items: T[], targetItem: T): void {
        this.currentActionRowIndex = items.findIndex(item => item === targetItem);
    }

    /**
     * Mark that focus restoration should happen after the next data reload
     */
    markForFocusRestoration (willRemoveRow: boolean): void {
        this.shouldRestoreFocus = willRemoveRow;
    }

    /**
     * Check if focus restoration is needed and reset the flag
     */
    shouldRestoreFocusAfterReload (): boolean {
        const shouldRestore = this.shouldRestoreFocus;
        this.shouldRestoreFocus = false;
        return shouldRestore;
    }

    /**
     * Restore focus to the appropriate row after data has been reloaded
     */
    restoreFocusToTableRow (table: Table): void {
        if (!table || this.currentActionRowIndex === -1) {
            return;
        }

        // Find the table body rows
        const nativeElement = table.el?.nativeElement as HTMLElement | undefined;
        if (!nativeElement) {
            this.currentActionRowIndex = -1;
            return;
        }

        const tableElement = nativeElement.querySelector('table') as HTMLTableElement | null;
        if (!tableElement) {
            this.currentActionRowIndex = -1;
            return;
        }

        const bodyRows = tableElement.querySelectorAll('tbody tr');
        if (!bodyRows || bodyRows.length === 0) {
            this.currentActionRowIndex = -1;
            return;
        }

        let targetRowIndex = this.currentActionRowIndex;

        // If the target row index is beyond the available rows, focus the last row
        if (targetRowIndex >= bodyRows.length) {
            targetRowIndex = bodyRows.length - 1;
        }

        // If we still have a valid target row, focus the first focusable column in that row
        if (targetRowIndex >= 0 && targetRowIndex < bodyRows.length) {
            const targetRow = bodyRows[targetRowIndex];
            const allCells = targetRow.querySelectorAll('td');

            // Try to find the first column with focusable content
            for (const cell of allCells) {
                const focusableElement = cell.querySelector('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement | null;

                if (focusableElement) {
                    focusableElement.focus();
                    this.currentActionRowIndex = -1;
                    return;
                }
            }

            // If no focusable elements found, make the first cell focusable and focus it
            const firstCell = allCells[0] as HTMLElement | null;
            if (firstCell) {
                firstCell.setAttribute('tabindex', '0');
                firstCell.focus();
                // Remove tabindex after focus to not interfere with normal tab order
                setTimeout(() => {
                    firstCell.removeAttribute('tabindex');
                }, 0);
            }
        }

        // Reset the action row index
        this.currentActionRowIndex = -1;
    }

    /**
     * Complete workflow helper for handling focus management during table actions
     */
    handleTableAction<TItem, TAction>(
        items: TItem[],
        targetItem: TItem,
        action: TAction,
        willRemoveRowPredicate: TableActionPredicate<TAction>,
    ): void {
        this.trackActionRowIndex(items, targetItem);
        const willRemoveRow = willRemoveRowPredicate(action);
        this.markForFocusRestoration(willRemoveRow);
    }

    /**
     * Helper for post-reload focus restoration (use in component data loading methods)
     */
    handlePostReloadFocus (table: Table): void {
        if (this.shouldRestoreFocusAfterReload()) {
            setTimeout(() => {
                this.restoreFocusToTableRow(table);
            }, 100);
        }
    }

    /**
     * Reset all focus management state
     */
    reset (): void {
        this.currentActionRowIndex = -1;
        this.shouldRestoreFocus = false;
    }
}
